/**
 * Convert hex color to HSL format for Tailwind CSS
 * Tailwind expects HSL values in format: "h s% l%" (without hsl() wrapper)
 */

export const hexToHsl = (hex: string): string => {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Parse RGB values
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h: number = 0;
  let s: number = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  // Format for Tailwind: "h s% l%"
  const hValue = Math.round(h * 360);
  const sValue = Math.round(s * 100);
  const lValue = Math.round(l * 100);

  return `${hValue} ${sValue}% ${lValue}%`;
};

export const convertColorToHSL = (color: string): string => {
  // If already in HSL format (contains %), return as is
  if (color.includes('%')) {
    // If it's in format "hsl(h s% l%)", extract just the values
    const hslMatch = color.match(/hsl\(([^)]+)\)/);
    if (hslMatch) {
      return hslMatch[1];
    }
    // If it's already in "h s% l%" format, return as is
    if (color.match(/^\d+\s+\d+%\s+\d+%$/)) {
      return color;
    }
  }

  // If it's a hex color, convert it
  if (color.startsWith('#')) {
    return hexToHsl(color);
  }

  // If it's rgb/rgba, convert to hex first then to HSL
  const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (rgbMatch) {
    const r = parseInt(rgbMatch[1]);
    const g = parseInt(rgbMatch[2]);
    const b = parseInt(rgbMatch[3]);
    const hex = `#${[r, g, b].map(x => {
      const hex = x.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    }).join('')}`;
    return hexToHsl(hex);
  }

  // If it's oklch, try to parse it (simplified - may need full oklch support)
  if (color.startsWith('oklch')) {
    // For now, return as is - oklch support can be added later
    return color;
  }

  // Return as is if we can't determine the format
  return color;
};

